import { Button, Checkbox, FormControl, FormLabel, Input, Select, Spacer, Stack, Tag, Text } from "@chakra-ui/react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { AuthContext } from "contexts/AuthContext";
import { useContent } from "hooks/useContent";
import { useContentUsage } from "hooks/useContentUsage";
import MultiSelectFilter from "components/filter/MultiSelectFilter";
import { matchesResolutions, resolutionArea, resolutionCounts, resolutionKey } from "lib/resolution";
import { formatSeconds, tokenFilter } from "lib/utils";
import { sumDurations } from "lib/broadcast";
import { contentTime } from "lib/content";
import { useContext, useEffect, useMemo, useState } from "react";
import { AvailableContentItem, SelectedContentItem } from "components/selection/ContentItems";
import { countAdded, EMPTY_LIST, makeEntries, sameList, SortButton } from "components/selection/common";

// - selected:    ids of the content in the playlist
// - onChange:    called with { items, duration }
// - resolutions: the resolutions the broadcast is made for (empty / missing = not limited). When
//                there are some, only videos and pictures with one of them are offered, until
//                "Show all content" is ticked. The playlist itself is never touched by this.
export default function ContentSelection({ selected, onChange, resolutions }) {

  const { user } = useContext(AuthContext)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [contentItems] = useContent({user})
  // Where each content item is played (green dot): the regular broadcasts that hold it
  const [usage] = useContentUsage()
  const [showAll, setShowAll] = useState(false)
  const [usedFilter, setUsedFilter] = useState([])
  // Chosen resolutions of the filter above the list (empty = any); not the same as the
  // resolutions of the broadcast, which decide what is offered at all
  const [resolutionFilter, setResolutionFilter] = useState([])
  const limited = Array.isArray(resolutions) && resolutions.length > 0
  const [filterTerm, setFilter] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [sortKey, setSortKey] = useState('-timestamp')

  // The playlist: one entry per position, { uid, id } (see makeEntries)
  const [entries, setEntries] = useState(() => makeEntries(selected || EMPTY_LIST))

  const itemsById = useMemo(() => new Map(contentItems.map(i => [i.id, i])), [contentItems])

  const selectionDuration = (ids) => sumDurations(ids, id => itemsById.get(id)?.duration)

  // Follow the parent when it hands us a different list (e.g. another broadcast was
  // opened). Echoes of our own changes are equal by value and keep the entries as they are.
  useEffect(() => {
    const next = selected || EMPTY_LIST
    setEntries(current => sameList(current.map(e => e.id), next) ? current : makeEntries(next))
  }, [selected])

  const updateSelection = (list) => {
    const ids = list.map(e => e.id)
    setEntries(list)
    onChange({
      items: ids,
      duration: selectionDuration(ids)
    })
  }

  const addToSelection = (id) => updateSelection([...entries, ...makeEntries([id], true)])

  const removeFromSelection = (index) => updateSelection(entries.filter((_, i) => i !== index))

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = entries.findIndex(e => e.uid === active.id)
    const newIndex = entries.findIndex(e => e.uid === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    updateSelection(arrayMove(entries, oldIndex, newIndex))
  }

  const sortContent = (a, b) => {
    const direction = sortKey.startsWith('-') ? -1 : 1
    const key = sortKey.replace('-', '')
    // The date is the real upload time, also for records that have no stored `timestamp`
    const valueOf = (item) => key === 'timestamp' ? contentTime(item) : (key === 'resolution' ? resolutionArea(resolutionKey(item)) : item[key])
    const av = valueOf(a) || 0
    const bv = valueOf(b) || 0
    if (av > bv) return 1 * direction
    if (av < bv) return -1 * direction
    return 0
  }

  const setSortContent = (key) => {
    if (sortKey.includes(key)) {
      setSortKey(sortKey.startsWith('-') ? key : '-' + key)
    } else {
      setSortKey('-' + key)
    }
  }

  // How many times each item is already in the playlist
  const counts = useMemo(() => entries.reduce((s, e) => ({ ...s, [e.id]: (s[e.id] || 0) + 1 }), {}), [entries])

  // Wait for the content to load before showing the playlist, so entries are not
  // briefly reported as missing.
  const sortedItems = contentItems.length === 0 ? [] : entries.map(e => ({
    ...(itemsById.get(e.id) || { id: e.id, missing: true }),
    sortKey: e.uid,
    isNew: !!e.added
  }))
  const addedCount = countAdded(entries)

  // What is offered: matches the search (name or resolution), the resolutions of the broadcast
  // (unless everything is shown) and the "in a broadcast" filter
  const pool = useMemo(
    () => limited && !showAll ? contentItems.filter(i => matchesResolutions(i, resolutions)) : contentItems,
    [contentItems, limited, showAll, resolutions]
  )
  // The resolutions of what is offered, with how many items have each (like on the Content page)
  const resolutionOptions = useMemo(() => [
    ...resolutionCounts(pool).map(item => ({ value: item.key, label: `${item.key} (${item.count})` })),
    { value: 'none', label: 'No resolution known' },
  ], [pool])
  const offered = pool.filter(i => {
    if (usedFilter.length > 0 && !usedFilter.includes(usage.has(i.id) ? 'in' : 'out')) return false
    if (resolutionFilter.length > 0 && !resolutionFilter.includes(resolutionKey(i) || 'none')) return false
    return true
  })
  const matchesSearch = (i) => tokenFilter(`${i.displayName || i.filename} ${resolutionKey(i) || ''}`, filterTerm)
  const filteredContent = offered
        .filter(matchesSearch)
        .sort(sortContent)

  const pageCount = Math.max(1, Math.ceil(filteredContent.length / pageSize))
  const currentPage = Math.min(page, pageCount - 1)
  const filteredItems = filteredContent.slice(currentPage * pageSize, currentPage * pageSize + pageSize)

  return (
    <>
      <FormControl>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={0} gap={{ base: 6, md: 0 }} mt={4}>
          <Stack direction={'column'} spacing={2} w={{ base: '100%', md: '50%' }} pr={{ base: 0, md: 4 }}>
            <Stack direction={'row'} w='100%' wrap='wrap' gap={2} spacing={0}>
              <FormLabel>Available content ({filteredContent.length} / {pool.length})</FormLabel>
              <Spacer />
              <Stack direction={'row'} spacing={1} wrap='wrap'>
                <SortButton label='size' field='size' sortKey={sortKey} onSort={setSortContent} />
                <SortButton label='resolution' field='resolution' sortKey={sortKey} onSort={setSortContent} />
                <SortButton label='duration' field='duration' sortKey={sortKey} onSort={setSortContent} />
                <SortButton label='date' field='timestamp' sortKey={sortKey} onSort={setSortContent} />
                <Select
                  w='unset'
                  style={{borderRadius: '30px' }}
                  size='xs'
                  display='flex'
                  alignItems='center'
                  defaultValue='10'
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}>
                  <option value='10'>10</option>
                  <option value='20'>20</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                  <option value='200'>200</option>
                </Select>
              </Stack>
            </Stack>
            <Input placeholder='Search...' size='sm' h={8} value={filterTerm} onChange={e => { setFilter(e.target.value); setPage(0) }} />
            <Stack direction='row' alignItems='center' wrap='wrap' gap={2} spacing={0}>
              <MultiSelectFilter inDrawer label='Any resolution' width='170px'
                options={resolutionOptions}
                value={resolutionFilter}
                onChange={next => { setResolutionFilter(next); setPage(0) }} />
              <MultiSelectFilter inDrawer label='In broadcasts or not' width='190px'
                options={[
                  { value: 'in', label: 'In a broadcast (green)' },
                  { value: 'out', label: 'Not in any broadcast' },
                ]}
                value={usedFilter}
                onChange={next => { setUsedFilter(next); setPage(0) }} />
              {limited && (
                <Checkbox size='sm' colorScheme='brand' isChecked={showAll} onChange={e => { setShowAll(e.target.checked); setPage(0) }}>
                  Show all content
                </Checkbox>
              )}
            </Stack>
            {limited && !showAll && (
              <Text fontSize='xs' color='gray.500'>
                Only videos and pictures of {resolutions.join(', ')} are shown. Tick "Show all content" to see the rest.
              </Text>
            )}
            <Stack direction='column' w="100%">
              {filteredItems.map(item => (
                <AvailableContentItem key={item.id} item={item} count={counts[item.id] || 0} onAdd={addToSelection} usedIn={usage.get(item.id)} resolutions={resolutions} />
              ))}
              {filteredItems.length === 0 && <Text fontSize='sm' color='gray.400'>Nothing found</Text>}
            </Stack>
            <Stack direction='row' justify='center' align='center' spacing={3} pt={2}>
              <Button size='xs' variant='ghost' colorScheme='brand' onClick={() => setPage(p => Math.max(0, p - 1))} isDisabled={currentPage <= 0}>Previous</Button>
              <Text fontSize='sm' color='gray'>Page {currentPage + 1} / {pageCount}</Text>
              <Button size='xs' variant='ghost' colorScheme='brand' onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} isDisabled={currentPage >= pageCount - 1}>Next</Button>
            </Stack>
          </Stack>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Stack direction={'column'} spacing={2} w={{ base: '100%', md: '50%' }} pl={{ base: 0, md: 4 }}>
              <FormLabel>Broadcast playlist</FormLabel>
              <Stack direction={'row'} alignItems='center' minH={8} wrap='wrap' gap={2} spacing={0}>
                <Text>Total duration</Text>
                <Tag px={2} variant='subtle' colorScheme='cyan'>
                  {formatSeconds(sortedItems.reduce((s, i) => s + (i.duration || 0), 0) || 0)}
                </Tag>
                <Tag px={2} variant='subtle' colorScheme='gray'>{sortedItems.length} items</Tag>
                {addedCount > 0 && <Tag px={2} variant='subtle' colorScheme='brand' title='Highlighted below until the broadcast is saved'>{addedCount} added, not saved</Tag>}
              </Stack>
              <SortableContext
                items={sortedItems.map(i => i.sortKey)}
                strategy={verticalListSortingStrategy}
              >
                {sortedItems.map((item, index) => (
                  <SelectedContentItem key={item.sortKey} item={item} position={index + 1} onRemove={removeFromSelection} resolutions={resolutions} />
                ))}
              </SortableContext>
            </Stack>
          </DndContext>
        </Stack>
      </FormControl>
    </>
  );
}

